/**
 * x-faq-search directive.
 *
 * Provides client-side filtering for FAQ items. As the user types, items with
 * a `data-faq-search-text` attribute are shown or hidden based on whether their
 * text contains the search terms. Categories that have no visible items are also
 * hidden. An accessible status message is updated after each filter pass.
 *
 * @options
 *   resultsMessage   - Message template shown when matches are found. Use
 *                      `{{ count }}` as a placeholder for the match count.
 *   noResultsMessage - Message shown when no items match the search terms.
 *
 * @example
 *   <div x-faq-search="{ resultsMessage: '{{ count }} results', noResultsMessage: 'No results found.' }">
 *     <input x-ref="searchInput" x-model="terms" type="search">
 *     <div data-faq-search-text="How do I return an item?">…</div>
 *   </div>
 */
(() => {
  const root = (Alpine, el, { resultsMessage, noResultsMessage }) => {
    Alpine.bind(el, () => ({
      ["x-data"]() {
        return {
          terms: "",
          matchCount: 0,
          totalCount: 0,
          statusMessage: "",

          /**
           * Returns true when the search input is empty.
           * @returns {boolean} True when no search terms have been entered.
           */
          get isEmpty() {
            return this.terms.length === 0;
          },

          /**
           * Counts the total number of searchable items and watches for term changes.
           */
          init() {
            this.totalCount = this.$el.querySelectorAll(
              "[data-faq-search-text]",
            ).length;

            this.$watch("terms", () => {
              this.filterItems();
            });
          },

          /**
           * Clears the search terms and returns focus to the search input.
           */
          clear() {
            this.terms = "";
            this.$refs.searchInput.focus();
          },

          /**
           * Shows or hides FAQ items and categories based on the current search terms.
           * Updates `matchCount` and the accessible status message after filtering.
           */
          filterItems() {
            const searchTerms = this.terms.toLowerCase().trim();
            const items = this.$el.querySelectorAll("[data-faq-search-text]");
            const categories = this.$el.querySelectorAll(".store-faq-category");
            let visibleCount = 0;

            items.forEach((item) => {
              const searchText = item
                .getAttribute("data-faq-search-text")
                .toLowerCase();
              const matches =
                searchTerms === "" || searchText.includes(searchTerms);

              item.style.display = matches ? "" : "none";

              if (matches) {
                visibleCount++;
              }
            });

            categories.forEach((category) => {
              const visibleItems = category.querySelectorAll(
                '[data-faq-search-text]:not([style*="display: none"])',
              );
              category.style.display = visibleItems.length > 0 ? "" : "none";
            });

            this.matchCount = visibleCount;
            this.updateStatusMessage();
          },

          /**
           * Updates the accessible status message based on the current match count.
           * Clears the message when the search input is empty.
           */
          updateStatusMessage() {
            if (this.isEmpty) {
              this.statusMessage = "";
            } else if (this.matchCount === 0) {
              this.statusMessage = noResultsMessage;
            } else {
              this.statusMessage = resultsMessage.replace(
                "{{ count }}",
                this.matchCount,
              );
            }
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("faq-search", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};
      root(Alpine, el, options);
    });
  });
})();
